import React from "react";
import SecurityModal from "./SecurityModal";
import Modal from "./Modal";
import SubmitHookForm from "./SubmitHookForm";
import CloseIconButton from "./CloseIconButton";
import CancelButton from "./CancelButton";
import PlusButton from "./PlusButton";
import MinusButton from "./MinusButton";
import { Controller } from "react-hook-form";
import SelectHookFormSingle from "./SelectHookFormSingle";
import SelectHookForm_3 from "./SelectHookForm_3";
import TextInputLabelHookForm from "./TextInputLabelHookForm";
import CheckBoxLabelHookForm from "./CheckBoxLabelHookForm";
import { RequiredInput } from "modules/CarrierAgreementMgmt/Security/atoms/atoms";
import { useRecoilState } from "recoil";

interface SecurityCameraFormProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "initialAdd" | "add" | "initialEdit" | "edit";
  isSaving?: boolean;
  formMethods: any;
  controlledFields: any[];
  append: (data: any) => void;
  remove: (index: number) => void;

  checkEmptyFields: (buttonState: string) => void;
  RemoveCamera: (id: any) => void;
  resetData: () => void;
  setCameraForEditFromOfficeData: (value: boolean) => void;

  dvrTypesOption: any[];
  cameraSystemOption: any[];
  camAndLocationOption: any[];
  camTypes1Option: any[];
  camTypes2Option: any[];

  cameras: any[];
  hasDeletePermission?: boolean;
  isEditState?: boolean;
  isAddState?: boolean;
  isAddInEditState?: boolean;
  isEditInAddState?: boolean;
  watch: any;
  setHasFirstFieldValue: (value: boolean) => void;
  setShowCameraModal: (value: boolean) => void;
  setShowCameraCardModal: (value: boolean) => void;
  onSubmit: (data: any) => void;
}

const SecurityCameraForm: React.FC<SecurityCameraFormProps> = ({
  isOpen,
  onClose,
  mode,
  isSaving = false,
  formMethods,
  controlledFields,
  append,
  remove,
  checkEmptyFields,
  RemoveCamera,
  resetData,
  setCameraForEditFromOfficeData,
  onSubmit,
  dvrTypesOption,
  cameraSystemOption,
  camAndLocationOption,
  camTypes1Option,
  camTypes2Option,
  cameras,
  hasDeletePermission,
  isEditState,
  watch,
  setShowCameraModal,
  setShowCameraCardModal,
}) => {
  const ModalComponent =
    mode === "initialAdd" || mode === "add" ? SecurityModal : Modal;

  const getTitle = () => {
    switch (mode) {
      case "initialAdd":
        return "Add Camera";
      case "add":
        return "Add Camera";
      case "initialEdit":
        return "Edit Camera";
      case "edit":
        return "Edit Camera";
      default:
        return "Camera Form";
    }
  };

  const getButtonText = () => {
    if (isSaving) return "Saving..";
    switch (mode) {
      case "initialAdd":
        return "Proceed";
      case "add":
        return "Save";
      case "initialEdit":
        return "Proceed";
      case "edit":
        return "Save";
      default:
        return "Submit";
    }
  };
  const [isRequired, setIsRequired] = useRecoilState(RequiredInput);

  const getFieldName = (index: number, fieldName: string) => {
    if (mode === "initialEdit" || mode === "edit") {
      if (
        fieldName === "camerasAndLocation" ||
        fieldName === "cameraType1Id" ||
        fieldName === "cameraType2Id"
      ) {
        return `camera.${index}.${fieldName}`;
      }
      return fieldName;
    }
    return `${cameras[0]?.tableName}[${index}].${fieldName}`;
  };

  const handleCancel = () => {
    if (mode === "edit") {
      setShowCameraCardModal(false);
      setCameraForEditFromOfficeData(false);
    } else {
      setShowCameraModal(false);
      resetData();
    }
  };

  const showCloseIcon = mode === "initialAdd" || mode === "initialEdit";
  const showCancelButton = mode === "edit" || mode === "add";

  const canAddMore = controlledFields?.length < 8;

  const handleAdd = () => {
    if (canAddMore) {
      checkEmptyFields("add");
      const hasExistingCameras =
        watch(`camera[0].camerasAndLocation`)?.length > 0;
      if (hasExistingCameras) {
        append({
          tableName: cameras[0]?.tableName,
          parentRow: false,
          ...(mode === "initialEdit" && { isNew: true }),
        });
      }
    }
  };

  const handleRemove = (index: number) => {
    remove(index);
    if (mode === "initialEdit" && controlledFields[index]?.id) {
      RemoveCamera(controlledFields[index].id);
    }
  };

  const getSecondDivClass = (index: number) => {
    return index < 1 && cameras[0]?.parentRow === true
      ? "grid grid-cols-1"
      : "";
  };

  return (
    <ModalComponent
      isCloseHide={true}
      modalFilterStat=""
      className="max-w-[900px]"
      width=""
      title=""
      isOpen={isOpen}
      onClose={undefined}
      hasCloseButton={false}
    >
      {/* Header */}
      <div className="flex flex-row whitespace-nowrap justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
        <div className="ml-2 font-bold text-primary text-md">{getTitle()}</div>

        <div className="flex justify-between gap-2 mr-2">
          <SubmitHookForm
            type="button"
            disabled={false}
            buttonText={getButtonText()}
            className={undefined}
            onClick={onSubmit}
          />
          {showCloseIcon && (
            <div className="relative -top-2 -right-6">
              <CloseIconButton
                onClick={() => {
                  setIsRequired(false);
                  onClose();
                }}
              />
            </div>
          )}

          {showCancelButton && (
            <CancelButton
              onClick={() => {
                setIsRequired(false);
                handleCancel();
              }}
            />
          )}
        </div>
      </div>

      <div className="w-full border-b-2 border-secondary mb-3"></div>

      {(mode === "initialAdd" || mode === "add") && (
        <div className="text-xs text-secondary italic font-normal mt-2 ml-2 mb-2">
          <b>Note:</b> All fields with * are required to fill.
        </div>
      )}

      {/* Form Content */}
      <div>
        {controlledFields?.map((field, index) => {
          delete controlledFields[index].id;

          return (
            <div key={index}>
              {index === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 p-4 lg:gap-12 gap-2">
                  {/* Left Column */}
                  <div className="space-y-1 mr-6">
                    <Controller
                      name={getFieldName(index, "cameraMake")}
                      control={formMethods.control}
                      render={({ field }) => (
                        <TextInputLabelHookForm
                          name={getFieldName(index, "cameraMake")}
                          label="Camera Make"
                          type="text"
                          register={formMethods.register(
                            getFieldName(index, "cameraMake"),
                            {}
                          )}
                          maxLength={20}
                          className={
                            formMethods.formState.errors.cameraId &&
                            "border-2 border-red-500 col-span-2"
                          }
                        />
                      )}
                    />

                    <TextInputLabelHookForm
                      name={getFieldName(index, "cameraModel")}
                      label="Camera Model"
                      type="text"
                      register={formMethods.register(
                        getFieldName(index, "cameraModel"),
                        {}
                      )}
                      maxLength={100}
                    />

                    <TextInputLabelHookForm
                      name={getFieldName(index, "cameraInstallDate")}
                      label="Install Date"
                      type="date"
                      register={formMethods.register(
                        getFieldName(index, "cameraInstallDate"),
                        {}
                      )}
                    />

                    <Controller
                      name={getFieldName(index, "dvrTypeId")}
                      control={formMethods.control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <SelectHookFormSingle
                          field={field}
                          onChange={(e) => field.onChange(e ? e.value : null)}
                          value={field.value}
                          options={dvrTypesOption}
                          label="DVR Type"
                          className={
                            formMethods.formState.errors.dvrtypeId &&
                            "border-2 border-red-500 col-span-2"
                          }
                          required={false}
                        />
                      )}
                    />

                    <TextInputLabelHookForm
                      name={getFieldName(index, "dvrRecordingMax")}
                      label="DVR Recording Max"
                      type="text"
                      register={formMethods.register(
                        getFieldName(index, "dvrRecordingMax"),
                        {}
                      )}
                      maxLength={100}
                    />
                  </div>

                  {/* Right Column */}
                  <div className="space-y-1 mr-2">
                    <Controller
                      name={getFieldName(index, "cameraSystemId")}
                      control={formMethods.control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <SelectHookFormSingle
                          field={field}
                          onChange={(e) => field.onChange(e ? e.value : null)}
                          value={field.value}
                          options={cameraSystemOption}
                          label="Camera System"
                          className={
                            formMethods.formState.errors.cameraSystemId &&
                            "border-2 border-red-500 col-span-2"
                          }
                          required={false}
                        />
                      )}
                    />

                    <TextInputLabelHookForm
                      name={getFieldName(index, "cameraIpAddress")}
                      label="IP Address"
                      type="text"
                      register={formMethods.register(
                        getFieldName(index, "cameraIpAddress"),
                        {}
                      )}
                      maxLength={100}
                    />

                    <TextInputLabelHookForm
                      name={getFieldName(index, "ipGateway")}
                      label="IP Gateway"
                      type="text"
                      register={formMethods.register(
                        getFieldName(index, "ipGateway"),
                        {}
                      )}
                      maxLength={100}
                    />

                    <TextInputLabelHookForm
                      name={getFieldName(index, "subnetMask")}
                      label="Subnet Mask"
                      type="text"
                      register={formMethods.register(
                        getFieldName(index, "subnetMask"),
                        {}
                      )}
                      maxLength={100}
                    />

                    <TextInputLabelHookForm
                      name={getFieldName(index, "dns")}
                      label="DNS"
                      type="text"
                      register={formMethods.register(
                        getFieldName(index, "dns"),
                        {}
                      )}
                      maxLength={100}
                    />

                    {mode === "edit" && isEditState && hasDeletePermission && (
                      <CheckBoxLabelHookForm
                        name="isActive"
                        type="checkbox"
                        register={formMethods.register}
                        label="Active Account"
                        className="cursor-pointer auto-rows-auto text-right justify-items-center"
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="w-full border-b-2 border-grey-200 my-3"></div>

              {/* Camera location and types section */}
              <div className="grid grid-cols-1 md:grid-cols-3 mx-1 mr-2 pb-2 lg:gap-12 gap-1">
                <div className="flex flex-row gap-2">
                  <div>
                    {index > 0 ? (
                      <MinusButton
                        className="mr-5"
                        type="button"
                        onClick={() => handleRemove(index)}
                      />
                    ) : (
                      <PlusButton
                        type="button"
                        onClick={handleAdd}
                        className={
                          !canAddMore ? "opacity-50 hover:bg-primary" : ""
                        }
                      />
                    )}
                  </div>

                  <Controller
                    name={getFieldName(index, "camerasAndLocation")}
                    control={formMethods.control}
                    rules={{ required: true }}
                    render={({ field }) => {
                      const value = formMethods.watch(field.name);
                      const isEmpty = !value || value.length === 0;

                      return (
                        <SelectHookForm_3
                          label="Number of Cameras and Location"
                          field={field}
                          onChange={(e) => field.onChange(e)}
                          value={value}
                          options={camAndLocationOption}
                          isMulti={true}
                          className={`-mt-1 border ${
                            isRequired && isEmpty
                              ? "border-red-500"
                              : "border-slate-300"
                          }`}
                          validation={{ required: true }}
                          defaultValue={undefined}
                          isDisabled={false}
                          isLoading={false}
                          secondDiv={getSecondDivClass(index)}
                        />
                      );
                    }}
                  />
                </div>

                <div className="space-y-1">
                  <Controller
                    name={getFieldName(index, "cameraType1Id")}
                    control={formMethods.control}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => field.onChange(e ? e.value : null)}
                        value={field.value}
                        options={camTypes1Option}
                        label="Camera Type 1"
                        className={
                          formMethods.formState.errors.cameraType1Id &&
                          "border-2 border-red-500 col-span-2"
                        }
                        required={false}
                        secondDiv={getSecondDivClass(index)}
                      />
                    )}
                  />
                </div>

                <div className="space-y-1">
                  <Controller
                    name={getFieldName(index, "cameraType2Id")}
                    control={formMethods.control}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => field.onChange(e ? e.value : null)}
                        value={field.value}
                        options={camTypes2Option}
                        label="Camera Type 2"
                        className={
                          formMethods.formState.errors.cameraType2Id &&
                          "border-2 border-red-500 col-span-2"
                        }
                        required={false}
                        secondDiv={getSecondDivClass(index)}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(mode === "initialAdd" || mode === "add") && (
        <div>
          <div className="w-full border-b-2 border-grey-200"></div>
        </div>
      )}
    </ModalComponent>
  );
};

export default SecurityCameraForm;
